import { Bell, BellOff } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "../../lib/api";
import {
  browserPushOptedIn,
  browserPushSupported,
  enableBrowserPush,
  removeBrowserPush,
  saveBrowserSubscription,
} from "../../lib/push";

export function BrowserPushSettings({ scope }: { scope: string }) {
  const [key, setKey] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const supported = browserPushSupported();
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    const initialize = async () => {
      const { publicKey } = await apiFetch<{ publicKey: string | null }>(
        "/api/admin/web-push",
      );
      if (cancelled) return;
      setKey(publicKey);
      if (
        !publicKey ||
        !browserPushOptedIn(scope) ||
        Notification.permission !== "granted"
      )
        return;
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (cancelled || !subscription) return;
      await saveBrowserSubscription(subscription);
      if (!cancelled) setEnabled(true);
    };
    void initialize().catch(() => {
      if (!cancelled)
        setError(
          "Browser-Push konnte nicht geladen werden. Bitte die Seite neu laden.",
        );
    });
    return () => {
      cancelled = true;
    };
  }, [scope, supported]);
  const label = !supported
    ? "Browser-Push wird hier nicht unterstützt. Auf dem iPhone die Website zum Home-Bildschirm hinzufügen."
    : !key
      ? "Browser-Push ist noch nicht eingerichtet."
      : enabled
        ? "Push für Freigaben deaktivieren"
        : "Push für Freigaben aktivieren";
  const toggle = async () => {
    if (!key) return;
    setBusy(true);
    setError("");
    try {
      if (enabled) await removeBrowserPush(scope);
      else await enableBrowserPush(scope, key);
      setEnabled(!enabled);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Browser-Push konnte nicht geändert werden.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="admin-push-control">
      <button
        type="button"
        title={label}
        aria-label={label}
        aria-pressed={enabled}
        disabled={busy || !supported || !key}
        onClick={() => void toggle()}
      >
        {enabled ? (
          <Bell size={20} aria-hidden="true" />
        ) : (
          <BellOff size={20} aria-hidden="true" />
        )}
      </button>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
